import { useCallback, useMemo, useRef, type PointerEvent as ReactPointerEvent } from 'react';
import {
  createKeyframe,
  evaluateKeyframes,
  isScaleLinked,
  syncLinkedScaleKeyframes,
  type AnimatableProperty,
  type Clip,
  type Transform2D,
} from '@cinegrid/core';
import { useEditorStore } from '../../state/editorStore';
import {
  aspectAngleDeg,
  aspectDist,
  clamp,
  clientToFrameUv,
  layerAnchor,
  layerCorners,
  layerFitScale,
  layerTopMid,
  type Vec2,
} from './viewerTransformMath';

const KF_EPS = 0.001;
const VISUAL_KINDS = new Set(['video', 'image', 'solid', 'text']);

type DragMode = 'move' | 'scale' | 'rotate';

type DragState = {
  mode: DragMode;
  clipId: string;
  startUv: Vec2;
  startTransform: Transform2D;
  startAnchor: Vec2;
  startAngle: number;
  startDist: number;
  cornerIndex: number;
  scaleLinked: boolean;
  aspect: number;
};

function evaluatedTransform(clip: Clip, playhead: number): Transform2D {
  const transform = { ...clip.properties.transform };
  const local = playhead - clip.start;
  const kfs = clip.properties.keyframes;
  for (const prop of [
    'opacity',
    'positionX',
    'positionY',
    'scaleX',
    'scaleY',
    'rotation',
  ] as const) {
    const v = evaluateKeyframes(kfs, prop, local);
    if (v != null) (transform as Record<string, number>)[prop] = v;
  }
  return transform;
}

function upsertKeyframes(
  keyframes: Clip['properties']['keyframes'],
  props: AnimatableProperty[],
  localTime: number,
  values: Record<string, number>
) {
  let next = [...keyframes];
  for (const p of props) {
    if (!(p in values)) continue;
    next = next.filter(
      (k) => !(k.property === p && Math.abs(k.time - localTime) < KF_EPS)
    );
    next.push(createKeyframe(p, localTime, values[p]!));
  }
  return next;
}

function commitTransformPatch(clipId: string, patch: Partial<Transform2D>) {
  const store = useEditorStore.getState();
  const clip = store.project.clips.find((c) => c.id === clipId);
  if (!clip) return;

  const playhead = store.playhead;
  const localTime = Math.max(0, playhead - clip.start);
  const kfs = clip.properties.keyframes;
  const nextTransform = { ...clip.properties.transform, ...patch };

  const animatedOrKeyed = (p: AnimatableProperty) =>
    kfs.some((k) => k.property === p);

  const propsToKey: AnimatableProperty[] = [];
  const values: Record<string, number> = {};
  for (const key of Object.keys(patch) as (keyof Transform2D)[]) {
    if (key === 'opacity' || key === 'anchorX' || key === 'anchorY') continue;
    const prop = key as AnimatableProperty;
    if (animatedOrKeyed(prop)) {
      propsToKey.push(prop);
      values[prop] = nextTransform[key] as number;
    }
  }

  if (propsToKey.length === 0) {
    store.updateClipTransform(clipId, patch);
    return;
  }

  let nextKfs = upsertKeyframes(kfs, propsToKey, localTime, values);
  if (
    isScaleLinked(clip.properties) &&
    (propsToKey.includes('scaleX') || propsToKey.includes('scaleY'))
  ) {
    nextKfs = syncLinkedScaleKeyframes(nextKfs);
  }

  store.updateClipProperties(clipId, {
    transform: nextTransform,
    keyframes: nextKfs,
  });
}

function resolveSourceSize(
  clip: Clip,
  assets: { id: string; width?: number; height?: number; kind: string }[],
  frameW: number,
  frameH: number
): { width: number; height: number } | null {
  if (clip.kind === 'solid' || clip.kind === 'text' || clip.kind === 'adjustment') {
    return null;
  }
  const asset = clip.assetId ? assets.find((a) => a.id === clip.assetId) : undefined;
  if (!asset?.width || !asset?.height) {
    return { width: frameW, height: frameH };
  }
  return { width: asset.width, height: asset.height };
}

export function ViewerTransformOverlay({
  disabled,
}: {
  disabled?: boolean;
}) {
  const project = useEditorStore((s) => s.project);
  const playhead = useEditorStore((s) => s.playhead);
  const selection = useEditorStore((s) => s.selection);
  const overlayRef = useRef<HTMLDivElement>(null);
  const dragRef = useRef<DragState | null>(null);

  const clip = useMemo(() => {
    const id = selection[0];
    if (!id) return null;
    const c = project.clips.find((x) => x.id === id);
    if (!c || !VISUAL_KINDS.has(c.kind)) return null;
    const track = project.tracks.find((t) => t.id === c.trackId);
    if (track?.locked) return null;
    return c;
  }, [project.clips, project.tracks, selection]);

  const frameW = project.settings.width;
  const frameH = project.settings.height;
  const aspect = frameW / Math.max(frameH, 1e-6);

  const live = useMemo(() => {
    if (!clip) return null;
    const transform = evaluatedTransform(clip, playhead);
    const sourceSize = resolveSourceSize(clip, project.assets, frameW, frameH);
    const fit = layerFitScale(sourceSize, frameW, frameH);
    const corners = layerCorners(transform, fit, aspect);
    const anchor = layerAnchor(transform, fit, aspect);
    const topMid = layerTopMid(transform, fit, aspect);
    const rotHandle = (() => {
      const dx = corners[1].x - corners[0].x;
      const dy = corners[1].y - corners[0].y;
      const len = Math.hypot(dx, dy) || 1;
      // Outward along top edge normal (into negative local Y before rotate ≈ above box)
      const nx = -dy / len;
      const ny = dx / len;
      // Flip if pointing toward center
      const toCenterX = anchor.x - topMid.x;
      const toCenterY = anchor.y - topMid.y;
      const outward = nx * toCenterX + ny * toCenterY > 0 ? -1 : 1;
      const offset = 0.04;
      return {
        x: topMid.x + nx * offset * outward,
        y: topMid.y + ny * offset * outward,
      };
    })();
    return { transform, fit, corners, anchor, topMid, rotHandle };
  }, [aspect, clip, frameH, frameW, playhead, project.assets]);

  const beginDrag = useCallback(
    (e: ReactPointerEvent, mode: DragMode, cornerIndex = 0) => {
      if (disabled || !clip || !live) return;
      e.preventDefault();
      e.stopPropagation();
      const el = overlayRef.current;
      if (!el) return;
      const rect = el.getBoundingClientRect();
      const uv = clientToFrameUv(e.clientX, e.clientY, rect);
      if (!uv) return;

      const drag: DragState = {
        mode,
        clipId: clip.id,
        startUv: uv,
        startTransform: { ...live.transform },
        startAnchor: live.anchor,
        startAngle: aspectAngleDeg(live.anchor, uv, aspect),
        startDist: Math.max(aspectDist(live.anchor, uv, aspect), 1e-6),
        cornerIndex,
        scaleLinked: isScaleLinked(clip.properties),
        aspect,
      };
      dragRef.current = drag;
      useEditorStore.getState().beginHistoryTransaction();

      const onMove = (ev: PointerEvent) => {
        const d = dragRef.current;
        if (!d) return;
        const r = overlayRef.current?.getBoundingClientRect();
        if (!r) return;
        const cur = clientToFrameUv(ev.clientX, ev.clientY, r);
        if (!cur) return;

        const { startTransform: st, startUv, startAnchor, aspect: a } = d;

        if (d.mode === 'move') {
          const dx = cur.x - startUv.x;
          const dy = cur.y - startUv.y;
          commitTransformPatch(d.clipId, {
            positionX: clamp(st.positionX + dx, -2, 2),
            positionY: clamp(st.positionY + dy, -2, 2),
          });
          return;
        }

        if (d.mode === 'rotate') {
          const angle = aspectAngleDeg(startAnchor, cur, a);
          let delta = angle - d.startAngle;
          while (delta > 180) delta -= 360;
          while (delta < -180) delta += 360;
          commitTransformPatch(d.clipId, {
            rotation: clamp(st.rotation + delta, -180, 180),
          });
          return;
        }

        const dist = Math.max(aspectDist(startAnchor, cur, a), 1e-6);
        const ratio = dist / d.startDist;
        if (d.scaleLinked) {
          const s = clamp(((st.scaleX + st.scaleY) / 2) * ratio, 0.05, 8);
          commitTransformPatch(d.clipId, { scaleX: s, scaleY: s });
        } else {
          commitTransformPatch(d.clipId, {
            scaleX: clamp(st.scaleX * ratio, 0.05, 8),
            scaleY: clamp(st.scaleY * ratio, 0.05, 8),
          });
        }
      };

      const onUp = () => {
        dragRef.current = null;
        useEditorStore.getState().endHistoryTransaction();
        window.removeEventListener('pointermove', onMove);
        window.removeEventListener('pointerup', onUp);
        window.removeEventListener('pointercancel', onUp);
      };

      window.addEventListener('pointermove', onMove);
      window.addEventListener('pointerup', onUp);
      window.addEventListener('pointercancel', onUp);
    },
    [aspect, clip, disabled, live]
  );

  if (!clip || !live || disabled) return null;

  const { corners, rotHandle, topMid } = live;
  const points = corners.map((c) => `${c.x},${c.y}`).join(' ');

  return (
    <div ref={overlayRef} className="pointer-events-none absolute inset-0 z-10 touch-none">
      <svg
        className="pointer-events-none absolute inset-0 h-full w-full overflow-visible"
        viewBox="0 0 1 1"
        preserveAspectRatio="none"
      >
        <polygon
          points={points}
          fill="rgba(34, 200, 216, 0.08)"
          stroke="rgba(34, 200, 216, 0.95)"
          strokeWidth={1.5}
          vectorEffect="non-scaling-stroke"
          className="pointer-events-auto cursor-move"
          onPointerDown={(e) => beginDrag(e, 'move')}
        />
        <line
          x1={topMid.x}
          y1={topMid.y}
          x2={rotHandle.x}
          y2={rotHandle.y}
          stroke="rgba(34, 200, 216, 0.85)"
          strokeWidth={1.25}
          vectorEffect="non-scaling-stroke"
        />
      </svg>

      <button
        type="button"
        title="Rotate"
        aria-label="Rotate"
        className="pointer-events-auto absolute h-3 w-3 -translate-x-1/2 -translate-y-1/2 cursor-grab rounded-full border border-[#0b1218] bg-accent shadow-sm outline-none active:cursor-grabbing"
        style={{ left: `${rotHandle.x * 100}%`, top: `${rotHandle.y * 100}%` }}
        onPointerDown={(e) => beginDrag(e, 'rotate')}
      />

      {corners.map((c, i) => (
        <button
          key={i}
          type="button"
          title="Scale"
          aria-label={`Scale corner ${i + 1}`}
          className="pointer-events-auto absolute h-2.5 w-2.5 -translate-x-1/2 -translate-y-1/2 cursor-nwse-resize rounded-[2px] border-2 border-accent bg-white shadow-sm outline-none"
          style={{ left: `${c.x * 100}%`, top: `${c.y * 100}%` }}
          onPointerDown={(e) => beginDrag(e, 'scale', i)}
        />
      ))}
    </div>
  );
}
